import { useEffect, useMemo, useRef, useState } from "react";
import { LocateFixed, MapPin, X } from "lucide-react";

export type MarketplaceMapItem = {
  id: number;
  hostId?: number | null;
  price: number;
  title: string;
  subtitle?: string;
  image?: string;
  rating?: number;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  neighborhood?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

type Props = {
  items: MarketplaceMapItem[];
  selectedId?: number | null;
  onSelect?: (id: number | null) => void;
  city?: string;
  className?: string;
};

type Coordinates = { lat: number; lng: number };
type CachedCoordinates = Coordinates & { savedAt: number };
type LeafletWindow = Window & { L?: any; __allepetsLeaflet?: Promise<any> };
type MarkerRecord = { marker: any; item: MarketplaceMapItem };

type LocationParts = {
  address: string;
  neighborhood: string;
  city: string;
  state: string;
};

const centers: Record<string, Coordinates> = {
  "aparecida de goiania": { lat: -16.8198, lng: -49.2469 },
  aracaju: { lat: -10.9472, lng: -37.0731 },
  belem: { lat: -1.4558, lng: -48.4902 },
  "belo horizonte": { lat: -19.9167, lng: -43.9345 },
  brasilia: { lat: -15.7939, lng: -47.8828 },
  "campo grande": { lat: -20.4697, lng: -54.6201 },
  campinas: { lat: -22.9056, lng: -47.0608 },
  cuiaba: { lat: -15.6014, lng: -56.0979 },
  curitiba: { lat: -25.4284, lng: -49.2733 },
  florianopolis: { lat: -27.5949, lng: -48.5482 },
  fortaleza: { lat: -3.7319, lng: -38.5267 },
  goiania: { lat: -16.6869, lng: -49.2648 },
  guarulhos: { lat: -23.4543, lng: -46.5337 },
  "joao pessoa": { lat: -7.1195, lng: -34.845 },
  jundiai: { lat: -23.1857, lng: -46.8978 },
  londrina: { lat: -23.3045, lng: -51.1696 },
  maceio: { lat: -9.6498, lng: -35.7089 },
  manaus: { lat: -3.119, lng: -60.0217 },
  marilia: { lat: -22.2171, lng: -49.9501 },
  maringa: { lat: -23.4205, lng: -51.9333 },
  natal: { lat: -5.7945, lng: -35.211 },
  niteroi: { lat: -22.8832, lng: -43.1034 },
  "porto alegre": { lat: -30.0346, lng: -51.2177 },
  recife: { lat: -8.0476, lng: -34.877 },
  "rio de janeiro": { lat: -22.9068, lng: -43.1729 },
  salvador: { lat: -12.9777, lng: -38.5016 },
  "sao paulo": { lat: -23.5505, lng: -46.6333 },
  sorocaba: { lat: -23.5015, lng: -47.4526 },
  vitoria: { lat: -20.3155, lng: -40.3128 },
};

const CACHE_PREFIX = "allepets:map-location:";
const CACHE_TTL = 180 * 24 * 60 * 60 * 1000;
const BRAZIL_CENTER: Coordinates = { lat: -14.235, lng: -51.9253 };

let geocodeQueue = Promise.resolve();
let lastGeocodeAt = 0;
let activeGeneration = 0;
const inFlightGeocodes = new Map<string, Promise<Coordinates | null>>();

function normalize(value?: string | null) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function baseCityName(value?: string | null) {
  return String(value || "")
    .split(",")[0]
    .split(" - ")[0]
    .trim();
}

function money(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function loadLeaflet() {
  const browserWindow = window as LeafletWindow;
  if (browserWindow.L) return Promise.resolve(browserWindow.L);
  if (browserWindow.__allepetsLeaflet) return browserWindow.__allepetsLeaflet;

  browserWindow.__allepetsLeaflet = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-allepets-leaflet]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.dataset.allepetsLeaflet = "1";
      document.head.appendChild(link);
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-allepets-leaflet]');
    if (existing) {
      if ((window as LeafletWindow).L) {
        resolve((window as LeafletWindow).L);
        return;
      }

      existing.addEventListener("load", () => resolve((window as LeafletWindow).L), { once: true });
      existing.addEventListener("error", () => reject(new Error("Falha ao carregar o mapa.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;
    script.dataset.allepetsLeaflet = "1";
    script.onload = () => resolve((window as LeafletWindow).L);
    script.onerror = () => reject(new Error("Falha ao carregar o mapa."));
    document.head.appendChild(script);
  });

  return browserWindow.__allepetsLeaflet;
}

function parseLocation(item: MarketplaceMapItem, fallbackCity?: string): LocationParts {
  const subtitleParts = String(item.subtitle || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    address: String(item.address || "").trim(),
    neighborhood: String(item.neighborhood || subtitleParts[0] || "").trim(),
    city: String(item.city || subtitleParts[1] || fallbackCity || "").trim(),
    state: String(item.state || "").trim(),
  };
}

function locationGroupKey(item: MarketplaceMapItem, fallbackCity?: string) {
  const location = parseLocation(item, fallbackCity);

  if (location.address) {
    return `address:${[location.address, location.neighborhood, location.city, location.state]
      .map(normalize)
      .filter(Boolean)
      .join("|")}`;
  }

  if (item.hostId) return `host:${item.hostId}`;

  return `item:${item.id}`;
}

function cacheKey(query: string) {
  return `${CACHE_PREFIX}${normalize(query).replace(/[^a-z0-9]+/g, "-")}`;
}

function readCache(query: string): Coordinates | null {
  try {
    const raw = localStorage.getItem(cacheKey(query));
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedCoordinates;
    if (!Number.isFinite(cached.lat) || !Number.isFinite(cached.lng)) return null;
    if (Date.now() - cached.savedAt > CACHE_TTL) return null;

    return { lat: cached.lat, lng: cached.lng };
  } catch {
    return null;
  }
}

function writeCache(query: string, coordinates: Coordinates) {
  try {
    localStorage.setItem(
      cacheKey(query),
      JSON.stringify({ ...coordinates, savedAt: Date.now() }),
    );
  } catch {
    // O mapa continua funcionando sem cache local.
  }
}

function geocode(query: string, generation: number): Promise<Coordinates | null> {
  const cached = readCache(query);
  if (cached) return Promise.resolve(cached);

  const requestKey = `${generation}:${cacheKey(query)}`;
  const existing = inFlightGeocodes.get(requestKey);
  if (existing) return existing;

  const task = geocodeQueue.then(async () => {
    if (generation !== activeGeneration) return null;

    const wait = Math.max(0, 1100 - (Date.now() - lastGeocodeAt));
    if (wait > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, wait));
    }

    if (generation !== activeGeneration) return null;
    lastGeocodeAt = Date.now();

    const params = new URLSearchParams({
      q: `${query}, Brasil`,
      format: "jsonv2",
      limit: "1",
      countrycodes: "br",
      "accept-language": "pt-BR",
    });

    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });

    if (!response.ok || generation !== activeGeneration) return null;

    const results = await response.json();
    const lat = Number(results?.[0]?.lat);
    const lng = Number(results?.[0]?.lon);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const coordinates = { lat, lng };
    writeCache(query, coordinates);
    return coordinates;
  });

  const tracked = task.finally(() => inFlightGeocodes.delete(requestKey));
  geocodeQueue = tracked.then(() => undefined, () => undefined);
  inFlightGeocodes.set(requestKey, tracked);

  return tracked.catch(() => null);
}

function cityDescriptor(items: MarketplaceMapItem[], requestedCity?: string) {
  const first = items.find((item) => item.city) || items[0];
  const city = baseCityName(requestedCity || first?.city || "");
  const state = String(first?.state || "").trim();
  const query = [city, state].filter(Boolean).join(", ");

  return { city, state, query };
}

function synchronousCityCenter(items: MarketplaceMapItem[], requestedCity?: string) {
  const descriptor = cityDescriptor(items, requestedCity);
  if (!descriptor.city) return null;

  return (
    centers[normalize(descriptor.city)] ||
    readCache(descriptor.query) ||
    readCache(descriptor.city)
  );
}

async function resolveCityCenter(
  items: MarketplaceMapItem[],
  requestedCity: string | undefined,
  generation: number,
): Promise<Coordinates> {
  const immediate = synchronousCityCenter(items, requestedCity);
  if (immediate) return immediate;

  const descriptor = cityDescriptor(items, requestedCity);
  if (descriptor.query) {
    const resolved = await geocode(descriptor.query, generation);
    if (resolved) return resolved;
  }

  return BRAZIL_CENTER;
}

async function baseCoordinates(
  item: MarketplaceMapItem,
  fallbackCity: string | undefined,
  cityCenter: Coordinates,
  generation: number,
): Promise<Coordinates> {
  if (Number.isFinite(item.latitude) && Number.isFinite(item.longitude)) {
    return { lat: Number(item.latitude), lng: Number(item.longitude) };
  }

  const location = parseLocation(item, fallbackCity);
  const addressQuery = [location.address, location.neighborhood, location.city, location.state]
    .filter(Boolean)
    .join(", ");
  const neighborhoodQuery = [location.neighborhood, location.city, location.state]
    .filter(Boolean)
    .join(", ");

  if (addressQuery) {
    const byAddress = await geocode(addressQuery, generation);
    if (byAddress) return byAddress;
  }

  if (neighborhoodQuery && generation === activeGeneration) {
    const byNeighborhood = await geocode(neighborhoodQuery, generation);
    if (byNeighborhood) return byNeighborhood;
  }

  return cityCenter;
}

function tinyOverlapOffset(position: number, total: number): Coordinates {
  if (total <= 1 || position === 0) return { lat: 0, lng: 0 };

  const ringPosition = position - 1;
  const angle = ringPosition * 137.508 * (Math.PI / 180);
  const radiusMeters = Math.min(18, 6 + Math.floor(ringPosition / 6) * 4);

  return {
    lat: (Math.sin(angle) * radiusMeters) / 111_000,
    lng: (Math.cos(angle) * radiusMeters) / 102_000,
  };
}

function createPriceIcon(L: any, item: MarketplaceMapItem, active: boolean) {
  return L.divIcon({
    className: "allepets-map-marker-wrap",
    html: `<button class="allepets-map-marker${active ? " is-active" : ""}">${money(item.price)}</button>`,
    iconSize: [74, 36],
    iconAnchor: [37, 18],
  });
}

export default function MarketplaceMapCanvas({
  items,
  selectedId,
  onSelect,
  city,
  className = "",
}: Props) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const userLayerRef = useRef<any>(null);
  const markersRef = useRef<Map<number, MarkerRecord>>(new Map());
  const onSelectRef = useRef(onSelect);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [positioningCity, setPositioningCity] = useState(true);
  const [markerProgress, setMarkerProgress] = useState({ done: 0, total: 0 });
  const [locating, setLocating] = useState(false);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId],
  );

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;
    let observer: ResizeObserver | null = null;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !nodeRef.current) return;

        const initialCenter = synchronousCityCenter(items, city);
        const map = L.map(nodeRef.current, {
          zoomControl: true,
          attributionControl: true,
          minZoom: 3,
          maxZoom: 18,
        });

        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
        }).addTo(map);

        layerRef.current = L.layerGroup().addTo(map);
        userLayerRef.current = L.layerGroup().addTo(map);
        map.setView(
          [initialCenter?.lat ?? BRAZIL_CENTER.lat, initialCenter?.lng ?? BRAZIL_CENTER.lng],
          initialCenter ? 12 : 4,
        );
        mapRef.current = map;

        observer = new ResizeObserver(() => map.invalidateSize(false));
        observer.observe(nodeRef.current);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));

    return () => {
      cancelled = true;
      observer?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (status !== "ready" || !mapRef.current || !layerRef.current) return;

    const generation = ++activeGeneration;
    let cancelled = false;
    const L = (window as LeafletWindow).L;
    if (!L) return;

    const render = async () => {
      setPositioningCity(true);
      setMarkerProgress({ done: 0, total: 0 });
      layerRef.current.clearLayers();
      markersRef.current.clear();

      const immediateCenter = synchronousCityCenter(items, city);
      if (immediateCenter) {
        mapRef.current.setView([immediateCenter.lat, immediateCenter.lng], 12, { animate: false });
        setPositioningCity(false);
      }

      const cityCenter = immediateCenter || await resolveCityCenter(items, city, generation);
      if (cancelled || generation !== activeGeneration) return;

      mapRef.current.setView([cityCenter.lat, cityCenter.lng], city ? 12 : 10, { animate: false });
      mapRef.current.invalidateSize(false);
      setPositioningCity(false);

      const groups = new Map<string, MarketplaceMapItem[]>();
      items.slice(0, 40).forEach((item) => {
        const key = locationGroupKey(item, city);
        const group = groups.get(key) || [];
        group.push(item);
        groups.set(key, group);
      });

      const entries = Array.from(groups.values());
      setMarkerProgress({ done: 0, total: entries.length });

      let completed = 0;
      await Promise.all(
        entries.map(async (group) => {
          const base = await baseCoordinates(group[0], city, cityCenter, generation);
          if (cancelled || generation !== activeGeneration) return;

          group.forEach((item, index) => {
            const offset = tinyOverlapOffset(index, group.length);
            const point = {
              lat: base.lat + offset.lat,
              lng: base.lng + offset.lng,
            };
            const marker = L.marker([point.lat, point.lng], {
              icon: createPriceIcon(L, item, selectedId === item.id),
              riseOnHover: true,
            });

            marker.on("click", () => onSelectRef.current?.(item.id));
            marker.addTo(layerRef.current);
            markersRef.current.set(item.id, { marker, item });
          });

          completed += 1;
          setMarkerProgress({ done: completed, total: entries.length });
        }),
      );

      window.setTimeout(() => mapRef.current?.invalidateSize(false), 50);
    };

    render();

    return () => {
      cancelled = true;
    };
  }, [city, items, status]);

  useEffect(() => {
    const L = (window as LeafletWindow).L;
    if (!L) return;

    markersRef.current.forEach(({ marker, item }, id) => {
      marker.setIcon(createPriceIcon(L, item, selectedId === id));
    });
  }, [selectedId]);

  const locate = () => {
    if (!navigator.geolocation || !mapRef.current) return;

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const L = (window as LeafletWindow).L;
        userLayerRef.current?.clearLayers();
        L.circleMarker([coords.latitude, coords.longitude], {
          radius: 8,
          color: "#fff",
          weight: 3,
          fillColor: "#64A002",
          fillOpacity: 1,
        }).addTo(userLayerRef.current);
        mapRef.current.setView([coords.latitude, coords.longitude], 13);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const markersLoading = markerProgress.total > 0 && markerProgress.done < markerProgress.total;

  return (
    <div
      className={`relative isolate min-h-[360px] overflow-hidden rounded-[28px] border border-slate-200 bg-slate-100 ${className}`}
      aria-label={`Mapa real com localização aproximada dos anfitriões${city ? ` em ${city}` : ""}`}
    >
      <style>{`
        .allepets-map-marker-wrap { background: transparent !important; border: 0 !important; }
        .allepets-map-marker {
          min-width: 66px; height: 34px; padding: 0 10px; border: 2px solid #fff;
          border-radius: 999px; background: #fff; color: #0f172a; cursor: pointer;
          font: 800 12px/30px system-ui, sans-serif; white-space: nowrap;
          box-shadow: 0 5px 16px rgba(15,23,42,.24); transition: .15s ease;
        }
        .allepets-map-marker:hover { transform: translateY(-2px) scale(1.04); }
        .allepets-map-marker.is-active { background: #FE5668; color: #fff; transform: scale(1.08); }
        .leaflet-container { font-family: inherit; background: #eef2e8; }
        .leaflet-control-attribution { font-size: 10px; }
      `}</style>

      <div ref={nodeRef} className="absolute inset-0" />

      <div className="pointer-events-none absolute left-4 top-4 z-[500] rounded-full bg-white/95 px-3 py-2 text-xs font-semibold text-slate-600 shadow-md backdrop-blur">
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-[#64A002]" />
          Localização aproximada
        </span>
      </div>

      {markersLoading && !positioningCity && (
        <div className="pointer-events-none absolute left-4 top-14 z-[500] rounded-full bg-white/95 px-3 py-2 text-xs font-semibold text-slate-500 shadow-md backdrop-blur">
          Posicionando anfitriões: {markerProgress.done}/{markerProgress.total}
        </div>
      )}

      <button
        type="button"
        onClick={locate}
        disabled={locating || status !== "ready"}
        className="absolute right-3 top-20 z-[500] grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white shadow-md disabled:opacity-50"
        aria-label="Usar minha localização"
      >
        <LocateFixed className={`h-4 w-4 ${locating ? "animate-pulse text-[#FE5668]" : "text-[#64A002]"}`} />
      </button>

      {(status === "loading" || positioningCity) && (
        <div className="absolute inset-0 z-[600] grid place-items-center bg-slate-100">
          <div className="rounded-2xl bg-white px-5 py-4 text-sm font-semibold text-slate-600 shadow-md">
            {status === "loading" ? "Carregando mapa…" : `Abrindo mapa${city ? ` de ${baseCityName(city)}` : ""}…`}
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="absolute inset-0 z-[600] grid place-items-center bg-slate-100 p-6">
          <div className="rounded-2xl border border-red-200 bg-white p-5 text-center shadow-md">
            <p className="font-bold text-slate-950">Não foi possível carregar o mapa</p>
            <p className="mt-2 text-sm text-slate-500">Verifique a conexão e recarregue a página.</p>
          </div>
        </div>
      )}

      {selected && (
        <div className="absolute bottom-6 left-4 right-4 z-[500] flex max-w-sm items-center gap-3 rounded-2xl border border-white/80 bg-white/95 p-3 pr-11 shadow-xl backdrop-blur md:right-auto md:w-[330px]">
          <button
            type="button"
            onClick={() => onSelect?.(null)}
            className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-[#FE5668]/40"
            aria-label="Fechar detalhes do anfitrião"
          >
            <X className="h-4 w-4" />
          </button>
          {selected.image && <img src={selected.image} alt="" className="h-16 w-20 shrink-0 rounded-xl object-cover" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-950">{selected.title}</p>
            {selected.subtitle && <p className="truncate text-xs text-slate-500">{selected.subtitle}</p>}
            <div className="mt-1 flex items-center justify-between gap-2 text-xs">
              <span>{selected.rating && selected.rating > 0 ? `★ ${selected.rating.toFixed(1)}` : "Novo anfitrião"}</span>
              <strong className="text-[#FE5668]">{money(selected.price)}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
